/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';
import { EntityModules, LookupModules } from '../moduleConfig';

export const namespaced = true;

export const state = {
  instruments: [],
  instrumentsLoading: false,
  userStaffLeadInstruments: [],
  userStaffLeadInstrumentsLoading: false,
  isInstrumentEdit: false,
  currentInstrument: {},
  instrumentProjectId: null,
  serverError: '',
  submitting: false,
  distinctStaffLeadNames: [],
  distinctStaffLeadNamesLoading: false,
  tagTypesLoading: false,
  submittingTagType: false,
};

export const actions = {
  async fetchInstruments({ state, dispatch }, projectId) {
    // only fetch if not already loaded
    if (state.instrumentProjectId !== projectId) {
      await dispatch('forceFetchInstruments', projectId);
    }
  },
  async forceFetchInstruments({ commit }, projectId) {
    commit('SET_INSTRUMENTS_LOADING', true);
    await api.get(`/project/${projectId}/instruments`)
      .then((response) => {
        commit('SET_INSTRUMENTS', response.data);
        commit('SET_INSTRUMENTS_LOADING', false);
        commit('SET_INSTRUMENT_PROJECT_ID', projectId);
      })
      .catch(() => {
        commit('SET_INSTRUMENTS_LOADING', false);
      });
  },
  async fetchInstrument({ commit, getters, dispatch }, { projectId, id }) {
    commit('SET_INSTRUMENT_EDIT', false);
    const instrument = getters.getInstrument(id);
    if (instrument) {
      commit('SET_CURRENT_INSTRUMENT', instrument);
    } else {
      // if instrument wasn't already loaded, load all instruments and try again
      // if not found this time, it's ok to set a nil instrument in state
      await dispatch('fetchInstruments', projectId);
      const instrumentSecondAttempt = getters.getInstrument(id);
      commit('SET_CURRENT_INSTRUMENT', instrumentSecondAttempt);
    }
  },
  async fetchPendingInstrumentsForCurrentUser({ commit }) {
    commit('SET_USER_STAFF_LEAD_LOADING', true);
    await api.get('/staffleadinstruments')
      .then((response) => {
        // we have seen some cases of the response being a string, so
        // we need to parse it if it comes back that way
        if (response.data instanceof Object) {
          commit('SET_USER_STAFF_LEAD_INSTRUMENTS', response.data);
        } else {
          commit('SET_USER_STAFF_LEAD_INSTRUMENTS', JSON.parse(response.data));
        }
        commit('SET_USER_STAFF_LEAD_LOADING', false);
      })
      .catch(() => {
        commit('SET_USER_STAFF_LEAD_LOADING', false);
      });
  },
  async deleteInstrument({ commit, dispatch, rootState }, { instrumentId, submittedCallback }) {
    commit('START_SUBMITTING');
    await api.delete(`/instrument/delete/${instrumentId}`)
      .then(() => {
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        const { id } = rootState.CurrentProject.currentProject;
        dispatch('forceFetchInstruments', id);
        dispatch('CurrentProject/fetchProjectDeleteMessage', { projectId: id }, { root: true });
        dispatch('CurrentProject/refreshLatestEvents', { projectId: id }, { root: true });
        submittedCallback();
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async updateEntity({ commit, dispatch, rootState }, { entity, submittedCallback }) {
    const submittedEntity = entity;
    submittedEntity.propertyInstruments = [];
    const endpoint = entity.isAssociatedPropertyUpdate !== undefined ? '/instrument/associatePropertiesToInstrument' : '/instrument/update';

    api.put(endpoint, submittedEntity)
      .then((response) => {
        commit('SET_CURRENT_INSTRUMENT', response.data);
        commit('SET_INSTRUMENT_EDIT', false);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        if (rootState.CurrentProject.currentProject) {
          const { id } = rootState.CurrentProject.currentProject;
          dispatch('forceFetchInstruments', id);
          dispatch('CurrentProject/clearCurrentProject', null, { root: true });
          dispatch('CurrentProject/fetch',
            { projectId: id },
            { root: true });
        }
        // there are things on the properties that will need to be refreshed once
        // an instrument has been edited
        dispatch('Property/resetProperties', {}, { root: true });
        if (response.data.events && response.data.events.length) {
          dispatch(`${EntityModules.INSTRUMENT_EVENT.namespace}/addNewEntity`,
            response.data.events[0], { root: true });
        }

        // refresh tag types to update counts
        dispatch(`${LookupModules.TAG_TYPES.namespace}/refreshOptions`,
          LookupModules.TAG_TYPES.url, { root: true });
        if (submittedCallback) submittedCallback();
      }).catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async associatePropertiesToInstrument({ commit, dispatch }, { entity, submittedCallback }) {
    const submittedEntity = entity;
    api.put('/instrument/associatePropertiesToInstrument', submittedEntity)
      .then((response) => {
        commit('SET_CURRENT_INSTRUMENT', response.data);
        commit('SET_INSTRUMENT_EDIT', false);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        // there are things on the properties that will need to be refreshed once
        // an instrument has been edited
        dispatch('Property/resetProperties', {}, { root: true });
        if (response.data.events && response.data.events.length) {
          dispatch(`${EntityModules.INSTRUMENT_EVENT.namespace}/addNewEntity`,
            response.data.events[0], { root: true });
        }
        if (submittedCallback) submittedCallback();
      }).catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async createEntity({ commit, dispatch, rootState }, { entity, submittedCallback }) {
    api.post('/instrument/create', entity)
      .then((response) => {
        api.get(`/instrument/${response.data}`)
          .then((getResponse) => {
            commit('ADD_NEW_INSTRUMENT', getResponse.data);
            commit('SET_SERVER_ERROR', '');
            commit('STOP_SUBMITTING');
            if (rootState.CurrentProject.currentProject) {
              const { id } = rootState.CurrentProject.currentProject;
              dispatch('forceFetchInstruments', id);
              dispatch('CurrentProject/clearCurrentProject', null, { root: true });
              dispatch('CurrentProject/fetch',
                { projectId: id },
                { root: true });
            }
            if (submittedCallback) submittedCallback(getResponse.data.id);
          })
          .catch((error) => {
            commit('SET_SERVER_ERROR', error.response.data);
            commit('STOP_SUBMITTING');
          });
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async updateDocumentFolder({ commit }, { id, folderId }) {
    commit('START_SUBMITTING');
    await api.patch(`/instrument/${id}/updateDocumentFolder`, { folderId })
      .then((response) => {
        commit('SET_DOCUMENT_FOLDER', response.data);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async startSubmitting({ commit }) {
    commit('START_SUBMITTING');
  },
  async stopSubmitting({ commit }) {
    commit('STOP_SUBMITTING');
  },
  switchToEdit({ commit }) {
    commit('SET_INSTRUMENT_EDIT', true);
  },
  stopEditing({ commit }) {
    commit('SET_INSTRUMENT_EDIT', false);
  },
  async fetchStaffLeadNames({ commit, state }) {
    // only fetch if not already loaded
    if (!state.distinctStaffLeadNames || state.distinctStaffLeadNames.length === 0) {
      commit('SET_STAFF_LEAD_LOADING', true);
      await api.get('/instrument/staffLeadNames')
        .then((response) => {
          // note, a list of strings is returned from the API, but we need it as
          // and object w/ name and id attributes
          // respect order in which users are returned from the backend
          commit('SET_STAFF_LEAD', response.data.map(sl => ({ name: sl, id: sl })));
          commit('SET_STAFF_LEAD_LOADING', false);
        })
        .catch(() => {
          commit('SET_STAFF_LEAD_LOADING', false);
        });
    }
  },
  async fetchInstrumentTagTypes({ commit }) {
    commit('SET_TAG_TYPES_LOADING', true);
    await api.get('/instrument/tagtypes')
      .then((response) => {
        commit('SET_TAG_TYPES', response.data);
        commit('SET_TAG_TYPES_LOADING', false);
      })
      .catch(() => {
        commit('SET_TAG_TYPES_LOADING', false);
      });
  },
  async startSubmittingTagType({ commit }) {
    commit('START_SUBMITTING_TAG_TYPE');
  },
  async stopSubmittingTagType({ commit }) {
    commit('STOP_SUBMITTING_TAG_TYPE');
  },
  async updateOrCreateTagType({ commit, dispatch }, { entity, submittedCallback }) {
    api.post('/instrument/updateOrCreateTagType', entity)
      .then((response) => {
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING_TAG_TYPE');
        dispatch(`${LookupModules.TAG_TYPES.namespace}/refreshOptions`,
          LookupModules.TAG_TYPES.url, { root: true });
        submittedCallback(response.data);
      }).catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING_TAG_TYPE');
      });
  },
  async createTagType({ commit, dispatch }, { entity, submittedCallback }) {
    api.post('/instrument/createTagType', entity)
      .then((response) => {
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING_TAG_TYPE');
        dispatch(`${LookupModules.TAG_TYPES.namespace}/refreshOptions`,
          LookupModules.TAG_TYPES.url, { root: true });
        submittedCallback(response.data);
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING_TAG_TYPE');
      });
  },
  async deleteTagType({ dispatch }, { id }) {
    api.delete(`Instrument/deleteTagType/${id}`)
      .then(() => {
        dispatch(`${LookupModules.TAG_TYPES.namespace}/refreshOptions`,
          LookupModules.TAG_TYPES.url, { root: true });
      });
  },
};

export const mutations = {
  SET_INSTRUMENT_EDIT(state, isInstrumentEdit) {
    state.isInstrumentEdit = isInstrumentEdit;
  },
  SET_SERVER_ERROR(state, serverError) {
    state.serverError = serverError;
  },
  SET_INSTRUMENTS_LOADING(state, loading) {
    state.instrumentsLoading = loading;
  },
  SET_INSTRUMENT_PROJECT_ID(state, projectId) {
    state.instrumentProjectId = projectId;
  },
  SET_INSTRUMENTS(state, instruments) {
    state.instruments = instruments;
  },
  SET_TAG_TYPES(state, tagTypes) {
    state.tagTypes = tagTypes;
  },
  ADD_NEW_INSTRUMENT(state, newObj) {
    state.instruments.push(newObj);
  },
  SET_CURRENT_INSTRUMENT(state, instrument) {
    state.currentInstrument = instrument;
    if (instrument) {
      // replace the instrument object in the array of instruments
      const currentInstrumentIndex = state.instruments.findIndex(inst => inst.id === instrument.id);
      if (currentInstrumentIndex >= 0) {
        state.instruments.splice(currentInstrumentIndex, 1, instrument);
      } else {
        state.instruments.splice(0, 0, instrument);
      }
    }
  },
  START_SUBMITTING_TAG_TYPE(state) {
    state.submittingTagType = true;
  },
  START_SUBMITTING(state) {
    state.submitting = true;
  },
  STOP_SUBMITTING_TAG_TYPE(state) {
    state.submittingTagType = false;
  },
  STOP_SUBMITTING(state) {
    state.submitting = false;
  },
  SET_DOCUMENT_FOLDER(state, folderId) {
    state.currentInstrument.documentFolderId = folderId;
  },
  SET_STAFF_LEAD(state, staffLeadList) {
    state.distinctStaffLeadNames = staffLeadList;
  },
  SET_STAFF_LEAD_LOADING(state, staffLeadLoading) {
    state.distinctStaffLeadNamesLoading = staffLeadLoading;
  },
  SET_USER_STAFF_LEAD_LOADING(state, loading) {
    state.userStaffLeadInstrumentsLoading = loading;
  },
  SET_USER_STAFF_LEAD_INSTRUMENTS(state, instruments) {
    state.userStaffLeadInstruments = instruments;
  },
  SET_TAG_TYPES_LOADING(state, loading) {
    state.tagTypesLoading = loading;
  },
};

export const getters = {
  orderedInstruments: state => state.instruments,
  getDefaultInstrument: (state) => {
    if (state.instruments.length > 0) {
      return getters.orderedInstruments(state)[0];
    }
    return null;
  },
  getInstrument: state => (id) => {
    if (id) return state.instruments.find(a => a.id === Number(id));
    return null;
  },
  instrumentCount: (state) => {
    if (state.instruments) return state.instruments.length;
    return 0;
  },
};
